import type { Address, Phone, ResidenceSection } from "@credit-acceptance/step6-bff-client";

import { APPLICANT_SSN_TYPES, type ApplicantSsnType, MAX_PHONES_PER_PERSON } from "./constants";
import { type CustomerInfoInput, makeEmptyCustomerInfo, makeEmptyPhone, type PhoneInput } from "./Signers.schema";

// ---------------------------------------------------------------------------
// residenceMapping
//
// Maps the BFF `ResidenceSection` (DscApplicantSection.residence) onto the
// form's `CustomerInfoInput` (personal details, physical/mailing address, and
// contact). Pure function — no React — so it stays unit-testable and reusable
// for both the Driver and 2nd Signer columns.
//
// SSN/ITIN: `ssnOrItin` (resolved from Customer Hub, step6-bff-client@1.0.287)
// hydrates the form's editable SSN/ITIN input as canonical digits. It is null
// when unavailable / no record / `isNoItinSsn`, in which case the field stays
// blank. The `isSsn` / `isNoItinSsn` flags drive `deriveApplicantSsnType`,
// which gates the Income Type list and clamps hydrated income types.
// ---------------------------------------------------------------------------

/**
 * Derives the applicant's SSN/ITIN status from the residence flags. Precedence:
 * `isNoItinSsn` (no valid SSN/ITIN on file) wins; otherwise `isSsn === true` is
 * an SSN; every other case (ITIN, or an unavailable/unknown status) falls to
 * "itin", matching dealer-ui's `isSsn ? "ssn" : "itin"` fallback. Non-SSN
 * statuses share the same restricted Income Type list.
 * @param {ResidenceSection | undefined} residence - The applicant's BFF residence section.
 * @returns {ApplicantSsnType} The applicant's SSN/ITIN status.
 */
export function deriveApplicantSsnType(residence?: ResidenceSection): ApplicantSsnType {
  if (residence?.isNoItinSsn) {
    return APPLICANT_SSN_TYPES.NO_ITIN_SSN;
  }
  if (residence?.isSsn === true) {
    return APPLICANT_SSN_TYPES.SSN;
  }
  return APPLICANT_SSN_TYPES.ITIN;
}

// BFF address-type id: 1 = CURRENT (physical), 2 = PREVIOUS, 3 = MAILING.
const PHYSICAL_ADDRESS_TYPE_ID = 1;
const MAILING_ADDRESS_TYPE_ID = 3;

/**
 * Finds the address matching a given type, by `addressType` string with a
 * fallback to the numeric `addressTypeId`.
 * @param {Address[] | undefined} addresses - The residence addresses.
 * @param {"PHYSICAL" | "MAILING"} type - The human-readable address type.
 * @param {number} typeId - The numeric address-type id fallback.
 * @returns {Address | undefined} The matching address, if any.
 */
function findAddress(
  addresses: Address[] | undefined,
  type: "PHYSICAL" | "MAILING",
  typeId: number
): Address | undefined {
  return addresses?.find((address) => address.addressType === type || address.addressTypeId === typeId);
}

/**
 * Maps a BFF phone onto the form's phone row. Only WORK (usage type 2) maps to
 * "Work"; every other usage type maps to "Personal" (the form's two options).
 * @param {Phone} phone - A BFF phone record.
 * @returns {PhoneInput} The hydrated phone row.
 */
function mapPhone(phone: Phone): PhoneInput {
  return {
    id: phone.id ?? crypto.randomUUID(),
    number: phone.number ?? "",
    type: phone.phoneUsageTypeId === 2 ? "Work" : "Personal",
    extension: phone.extension ?? "",
  };
}

/**
 * Maps the applicant's `ResidenceSection` onto the form's customer information.
 * Selects the physical/mailing addresses by type, orders phones/emails by
 * `displayOrder`, and falls back to form defaults for fields the BFF does not
 * carry. Returns blank customer info when the residence is absent.
 * @param {ResidenceSection | undefined} residence - The applicant's BFF residence section.
 * @returns {CustomerInfoInput} The hydrated customer information.
 */
export function mapResidenceSectionToCustomerInfo(residence?: ResidenceSection): CustomerInfoInput {
  const base = makeEmptyCustomerInfo();
  if (!residence) {
    return base;
  }

  const physical = findAddress(residence.addresses, "PHYSICAL", PHYSICAL_ADDRESS_TYPE_ID);
  const mailing = findAddress(residence.addresses, "MAILING", MAILING_ADDRESS_TYPE_ID);

  const phones = [...(residence.phones ?? [])]
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
    .slice(0, MAX_PHONES_PER_PERSON)
    .map((phone) => mapPhone(phone));

  // Always show Primary + Secondary rows, padding with blanks if the BFF
  // returned fewer than two phones.
  while (phones.length < 2) {
    phones.push(makeEmptyPhone());
  }

  // Polarity flip: the BFF carries `isEmailAvailable`, the form carries the
  // inverse `emailNotAvailable`. The write side flips it back in
  // `Signers.putMapping.ts` → `mapCustomerInfoToEmailsSection`.
  // Explicit `=== false` (not `!residence.isEmailAvailable`) so an unanswered
  // flag (undefined/null on a not-yet-touched applicant) still defaults to
  // unchecked rather than being conflated with an explicit "no email".
  const emailNotAvailable = residence.isEmailAvailable === false;

  // Blank the email whenever the flag says there is none. Hydrating a checked
  // box next to a stale address is a state the UI can't reach interactively
  // (the checkbox handler clears email), and the stale value would be silently
  // re-saved on the next PUT.
  const emailAddress = emailNotAvailable
    ? ""
    : ([...(residence.emailAddresses ?? [])].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))[0]
        ?.emailAddress ?? "");

  return {
    ...base,
    // Canonical digits only; SsnInput also strips, but keep the model clean.
    ssnOrItin: residence.ssnOrItin?.replaceAll(/\D/g, "") ?? "",
    firstName: residence.firstName ?? "",
    middleInitial: residence.middleName?.charAt(0) ?? "",
    lastName: residence.lastName ?? "",
    suffix: residence.nameSuffix ?? "",
    dateOfBirth: residence.dateOfBirth ?? "",
    physicalStreetNumber: physical?.streetNumber ?? "",
    physicalStreetName: physical?.streetName ?? "",
    physicalApartmentNumber: physical?.apartmentNumber ?? "",
    physicalZip: physical?.zip ?? "",
    physicalCity: physical?.city ?? "",
    physicalState: physical?.state ?? "",
    // No MAILING address means the mailing address mirrors the current one.
    mailingSameAsCurrent: !mailing,
    mailingStreetNumber: mailing?.streetNumber ?? "",
    mailingStreetName: mailing?.streetName ?? "",
    mailingApartmentNumber: mailing?.apartmentNumber ?? "",
    mailingZip: mailing?.zip ?? "",
    mailingCity: mailing?.city ?? "",
    mailingState: mailing?.state ?? "",
    phones,
    emailAddress,
    emailNotAvailable,
  };
}
